import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Tab,
  Table,
  Tabs
} from 'react-bootstrap';
import ApiService from '../../helpers/apiService';
import AmlTab from '../aml/AmlTab';
import AmlScenariosTab from './AmlScenariosTab';
import { AML_SCENARIOS } from '../../mocks/amlScenariosMocks';

interface Alert {
  id: string;
  name?: string;
  type?: string;
  severity?: string;
  message?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

interface LogEntry {
  // v49 fix-6: переход с supportGetLogs (501) на amlListEvents (aml_alert_events).
  id?: string;
  // AML event fields
  alert_id?: string;
  event_type?: string;
  description?: string;
  actor?: string;
  case_id?: string;
  user_name?: string;
  user_id?: string;
  created_at?: string;
  // Старые поля (для backward-compat если supportGetLogs вернётся)
  level?: string;
  message?: string;
  service?: string;
  timestamp?: string;
  [key: string]: any;
}

const REFRESH_INTERVAL = 30000;

const severityBadge = (severity?: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return <Badge bg="danger">Critical</Badge>;
    case 'high':
      return <Badge bg="warning" text="dark">High</Badge>;
    case 'medium':
      return <Badge bg="info">Medium</Badge>;
    case 'low':
      return <Badge bg="secondary">Low</Badge>;
    default:
      return <Badge bg="light" text="dark">{severity || 'N/A'}</Badge>;
  }
};

// v49 fix-6: levelBadge удалён — вкладка «Логи» теперь показывает AML-события
// (aml_alert_events), а не support-логи с level/service. Если понадобится вернуть
// service-логи — взять из git history до 2026-05-20.

const Finmonitoring: React.FC = () => {
  // v48 SH-1: если приходим из /scoring-history по ссылке с ?caseId=... —
  // сразу переключаемся на вкладку AML. AmlTab сам прочитает useSearchParams
  // и подгрузит нужный кейс.
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('caseId') || searchParams.get('alertId') ? 'aml' : 'alerts';
  const [activeTab, setActiveTab] = useState(initialTab);
  // Примечание: вкладка AML управляет своим состоянием самостоятельно (см. AmlTab.tsx)
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorCountRef = useRef(0);

  // Add alert modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAlert, setNewAlert] = useState({
    name: '',
    type: '',
    severity: 'low',
    message: '',
    scenarioCodes: [] as string[],
    channels: { telegram: false, email: false, api: false },
  });
  const [saving, setSaving] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const data = await ApiService.supportListAlerts<Alert[]>();
      setAlerts(Array.isArray(data) ? data : []);
      errorCountRef.current = 0;
    } catch {
      // Сервер недоступен — молча игнорируем (API в разработке)
      errorCountRef.current++;
      if (errorCountRef.current >= 3) setAutoRefresh(false);
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      // v49 fix-6: AML-события вместо устаревшего supportGetLogs (501).
      const data = await ApiService.amlListEvents<LogEntry[]>({ limit: 200 });
      setLogs(Array.isArray(data) ? data : []);
      errorCountRef.current = 0;
    } catch {
      errorCountRef.current++;
      if (errorCountRef.current >= 3) setAutoRefresh(false);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  const fetchData = useCallback(() => {
    if (activeTab === 'alerts') fetchAlerts();
    else fetchLogs();
  }, [activeTab, fetchAlerts, fetchLogs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleAddAlert = async () => {
    setSaving(true);
    try {
      await ApiService.supportAddAlerts(newAlert);
      setShowAddModal(false);
      setNewAlert({ name: '', type: '', severity: 'low', message: '', scenarioCodes: [], channels: { telegram: false, email: false, api: false } });
      fetchAlerts();
    } catch {
      // Молча — API в разработке
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlert = async (id: string) => {
    if (!window.confirm('Удалить алерт?')) return;
    try {
      await ApiService.supportDeleteAlerts({ id });
      fetchAlerts();
    } catch {
      // молча
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Финмониторинг</h3>
          <p className="mt-2 mb-0">Алерты и логи финансового мониторинга</p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {/* v49 fix-3: глобальный контрол Автообновление/Обновить применяется
              только к вкладкам Алерты и Логи. AML имеет собственный контрол
              автообновления внутри AmlTab (с независимым 30-сек интервалом и
              error-tolerant refresh), поэтому при activeTab='aml' прячем
              глобальный — иначе оператор видел дубль кнопок, а глобальная
              «Обновить» дергала пустые fetchAlerts/fetchLogs, не имеющие
              отношения к AML. */}
          {activeTab !== 'aml' && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Check
                type="switch"
                label={`Автообновление (${REFRESH_INTERVAL / 1000} сек)`}
                checked={autoRefresh}
                onChange={() => setAutoRefresh(!autoRefresh)}
              />
              <Button variant="outline-primary" size="sm" onClick={fetchData}>
                Обновить
              </Button>
            </div>
          )}

          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'alerts')} className="mb-3">
            {/* Вкладка AML — независимый компонент с реальными данными из БД (AlertWriter + ScoringPoller) */}
            <Tab eventKey="aml" title={<span>AML <Badge bg="danger" pill>8</Badge></span>}>
              <AmlTab />
            </Tab>

            <Tab eventKey="alerts" title="Алерты">
              <Tabs defaultActiveKey="alerts-list" className="mb-3" variant="pills">
                <Tab eventKey="alerts-list" title="Список алертов">
                  <div className="d-flex justify-content-end mb-2">
                    <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                      Добавить алерт
                    </Button>
                  </div>

                  {loadingAlerts ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                  ) : alerts.length === 0 ? (
                    <p className="text-muted text-center py-4">Нет алертов</p>
                  ) : (
                    <Table hover responsive size="sm" className="align-middle" style={{ fontSize: '0.82rem' }}>
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Имя</th>
                          <th>Тип</th>
                          <th>Severity</th>
                          <th>Сообщение</th>
                          <th>Статус</th>
                          <th>Создан</th>
                          <th>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.map((alert, idx) => (
                          <tr key={alert.id || idx}>
                            <td>{alert.id}</td>
                            <td>{alert.name || '-'}</td>
                            <td>{alert.type || '-'}</td>
                            <td>{severityBadge(alert.severity)}</td>
                            <td style={{ maxWidth: '300px' }} className="text-truncate">
                              {alert.message || '-'}
                            </td>
                            <td>{alert.status || '-'}</td>
                            <td>{alert.created_at || '-'}</td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteAlert(alert.id)}
                              >
                                Удалить
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Tab>

                <Tab
                  eventKey="scenarios"
                  title={
                    <span>
                      Сценарии AML{' '}
                      <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}>
                        15
                      </Badge>
                    </span>
                  }
                >
                  <AmlScenariosTab />
                </Tab>
              </Tabs>
            </Tab>

            <Tab eventKey="logs" title="Логи">
              {/* v49 fix-6: события AML — изменения транзакций (created / decision /
                  status_change / scoring_update) из aml_alert_events с join к
                  aml_alerts. Показываем case_id для дип-линка в кейс. */}
              {loadingLogs ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : logs.length === 0 ? (
                <p className="text-muted text-center py-4">Нет событий — в БД ещё нет записей в aml_alert_events.</p>
              ) : (
                <Table hover responsive size="sm" className="align-middle" style={{ fontSize: '0.82rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 150 }}>Время</th>
                      <th>Кейс</th>
                      <th>Тип события</th>
                      <th>Пользователь</th>
                      <th>Кто</th>
                      <th>Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => {
                      const eventTypeBadgeBg =
                        log.event_type === 'decision' ? 'success'
                          : log.event_type === 'status_change' ? 'info'
                          : log.event_type === 'scoring_update' ? 'warning'
                          : log.event_type === 'created' ? 'primary'
                          : 'secondary';
                      // Fallback: timestamp (старый support) > created_at (AML)
                      const time = log.created_at || log.timestamp;
                      return (
                        <tr key={log.id || idx}>
                          <td className="text-muted small">
                            {time ? new Date(time).toLocaleString('ru-RU') : '—'}
                          </td>
                          <td>
                            {log.case_id ? (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => {
                                  // Дип-линк через query — AmlTab подхватит и откроет кейс (см. v48 SH-1).
                                  setActiveTab('aml');
                                  const url = new URL(window.location.href);
                                  url.searchParams.set('caseId', log.case_id!);
                                  if (log.alert_id) url.searchParams.set('alertId', log.alert_id);
                                  window.history.replaceState({}, '', url.toString());
                                  // Принудительно триггерим re-render search-params reader
                                  window.dispatchEvent(new PopStateEvent('popstate'));
                                }}
                              >
                                {log.case_id}
                              </Button>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            <Badge bg={eventTypeBadgeBg}>{log.event_type || '—'}</Badge>
                          </td>
                          <td className="small">
                            {log.user_name || (log.user_id ? <code style={{ fontSize: '0.7rem' }}>{log.user_id.slice(0, 8)}…</code> : '—')}
                          </td>
                          <td className="small text-muted">{log.actor || '—'}</td>
                          <td style={{ maxWidth: '380px' }} className="text-truncate" title={log.description || log.message}>
                            {log.description || log.message || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Add Alert Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Добавить алерт</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Имя</Form.Label>
            <Form.Control
              value={newAlert.name}
              onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Тип</Form.Label>
            <Form.Control
              value={newAlert.type}
              onChange={(e) => setNewAlert({ ...newAlert, type: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Severity</Form.Label>
            <Form.Select
              value={newAlert.severity}
              onChange={(e) => setNewAlert({ ...newAlert, severity: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Сообщение</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newAlert.message}
              onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Сценарии AML <span className="text-muted" style={{ fontSize: '0.8rem' }}>(необязательно)</span></Form.Label>
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4, padding: '6px 10px' }}>
              {AML_SCENARIOS.map((s) => (
                <Form.Check
                  key={s.id}
                  type="checkbox"
                  id={`scn-${s.id}`}
                  label={<span style={{ fontSize: '0.8rem' }}><code className="me-1">{s.code}</code>{s.name}</span>}
                  checked={newAlert.scenarioCodes.includes(s.code)}
                  onChange={(e) => {
                    const codes = e.target.checked
                      ? [...newAlert.scenarioCodes, s.code]
                      : newAlert.scenarioCodes.filter((c) => c !== s.code);
                    setNewAlert({ ...newAlert, scenarioCodes: codes });
                  }}
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Каналы оповещения</Form.Label>
            <div className="d-flex gap-4">
              <Form.Check
                type="checkbox"
                id="ch-telegram"
                label="Telegram"
                checked={newAlert.channels.telegram}
                onChange={(e) => setNewAlert({ ...newAlert, channels: { ...newAlert.channels, telegram: e.target.checked } })}
              />
              <Form.Check
                type="checkbox"
                id="ch-email"
                label="Email"
                checked={newAlert.channels.email}
                onChange={(e) => setNewAlert({ ...newAlert, channels: { ...newAlert.channels, email: e.target.checked } })}
              />
              <Form.Check
                type="checkbox"
                id="ch-api"
                label="API Webhook"
                checked={newAlert.channels.api}
                onChange={(e) => setNewAlert({ ...newAlert, channels: { ...newAlert.channels, api: e.target.checked } })}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Отмена</Button>
          <Button variant="primary" onClick={handleAddAlert} disabled={saving}>
            {saving ? 'Сохранение...' : 'Создать'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Finmonitoring;
